import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { Loader2, RefreshCw, MapPin, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, LineChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Cell,
} from 'recharts';

/**
 * Yataş Grubu — Lokal Performans  (/gbp/lokal)
 *
 * Sidebar: Business Profile → Local Performance. 7 Eki 2026'da /yatas/planlama
 * "GBP × Lokal" sekmesinden ayrı sayfaya taşındı (Yataş toplantısı isteği):
 *   1) Google Business Profile (reklamlı + reklamsız tüm aksiyonlar)
 *   2) Reklamdan gelen lokasyon aksiyonları (Google Ads'in Google-hosted dönüşümleri)
 *   3) Bütçe olarak YALNIZ lokal kampanya harcaması + birlikte artıyor mu çizgisi
 * Backend: routes/yatasPlanning.js → GET /gbp-local?from=YYYY-MM&to=YYYY-MM (Yataş dışı 403).
 * 4 Kas 2026: ay aralığı filtresi + MARKETER (marka kullanıcısı) erişimi açıldı.
 * Tasarım panel diliyle (GoogleAdsDonusumKirilimi ile aynı tokenlar).
 */

const API = 'https://api.glowytics.com';

const YATAS_GROUP_BRAND_IDS = new Set([
  '44e35dfe-0186-495d-a0cc-db5e7e459ab7', // Yataş Bedding
  'dfcf54dd-b00d-434a-be1e-27b6484848eb', // Divanev
  '7fc8e68d-f2ba-4101-95f7-acece386ab8f', // Puffy
  'ccb2364b-e4e1-4f7f-944a-44cc9a839c6e', // EnzaHome
]);

const C = {
  ground: '#F6F8FF', card: '#FFFFFF', line: '#E5E7EB', hair: '#F3F4F6', head: '#FAFBFF',
  ink: '#111827', ink2: '#374151', muted: '#6B7280', faint: '#9CA3AF',
  accent: '#436AEB', accentSoft: '#EEF2FE', warn: '#B45309', warnSoft: '#FFF7ED', up: '#15803D', down: '#B91C1C',
};
const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
// Her metrik sayfanın her yerinde aynı renk
const M = {
  directions: '#436AEB', calls: '#1baf7a', website: '#eda100', storeVisits: '#9b59d0', cost: '#eb6834', adLocal: '#0ea5b7',
};

const MONTHS_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const monthLabel = (ym) => (ym ? `${MONTHS_TR[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}` : '');
const shortMonth = (ym) => (ym ? `${MONTHS_TR[Number(ym.slice(5, 7)) - 1].slice(0, 3)} ${ym.slice(2, 4)}` : '');

const nf = (v, d = 0) => Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });
const num = (v) => (v == null ? '–' : nf(v));
const tl = (v) => (v == null ? '–' : `₺${nf(v)}`);
const compact = (v) => {
  if (v == null) return '–';
  const a = Math.abs(v);
  if (a >= 1e6) return `${nf(v / 1e6, 1)}M`;
  if (a >= 1e3) return `${nf(v / 1e3)}B`;
  return nf(v);
};
// 'YYYY-MM-DD' → 'DD.MM'
const ddmm = (d) => (d && /^\d{4}-\d{2}-\d{2}/.test(d) ? `${d.slice(8, 10)}.${d.slice(5, 7)}` : null);
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' }) : null);
const TRIGGER_TR = { cron: 'otomatik', manual: 'elle', 'manual-script': 'elle' };
const PERMISSION_ERR = 'The caller does not have permission';
const pctChange = (cur, prev) => (cur == null || !prev ? null : (cur - prev) / prev);

async function getJson(path, brandId, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: 'include', headers: { 'X-Brand-ID': brandId }, ...opts,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

/* ── Panel parçaları ─────────────────────────────────────────────── */

const kart = { background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04)' };
const th = { textAlign: 'right', padding: '9px 12px', fontSize: 12, fontWeight: 600, color: C.muted, borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap', background: C.head, position: 'sticky', top: 0 };
const td = { textAlign: 'right', padding: '9px 12px', fontSize: 13, color: C.ink2, borderBottom: `1px solid ${C.hair}`, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
const axisTick = { fontSize: 11, fill: C.faint };
const tooltipStyle = { borderRadius: 10, border: `1px solid ${C.line}`, fontFamily: FONT, fontSize: 12, boxShadow: '0 4px 12px rgba(16,24,40,.08)' };

// tone: 'good-up' (varsayılan) · 'good-down' (maliyet: düşmesi iyi) · 'neutral' (harcama: iyi/kötü değil)
function Change({ v, tone = 'good-up' }) {
  if (v == null) return null;
  const up = v >= 0;
  const good = tone === 'good-down' ? !up : up;
  const color = tone === 'neutral' ? C.muted : good ? C.up : C.down;
  const bg = tone === 'neutral' ? C.hair : good ? '#ECFDF3' : '#FEF2F2';
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color, background: bg, borderRadius: 6, padding: '2px 6px' }}>
      {up ? '▲' : '▼'} %{nf(Math.abs(v * 100))}
    </span>
  );
}

function Kpi({ label, value, change, tone, sub, color }) {
  return (
    <div style={{ ...kart, padding: '16px 18px', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: C.muted }}>
        {color && <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 26, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        <Change v={change} tone={tone} />
      </div>
      {sub && <div style={{ fontSize: 12, color: C.faint, marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

function Section({ step, title, sub, right, children }) {
  return (
    <section style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.accent, letterSpacing: '.02em' }}>{step}</div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: C.ink, margin: '2px 0 0' }}>{title}</h2>
          {sub && <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>{sub}</div>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function Panel({ title, legend, children, style }) {
  return (
    <div style={{ ...kart, padding: 18, minWidth: 0, ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>{title}</div>
        {legend && (
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {legend.map(([label, color, dashed]) => (
              <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.ink2 }}>
                <span style={{ width: 12, height: dashed ? 0 : 8, borderRadius: 2, background: dashed ? 'none' : color, borderTop: dashed ? `2px dashed ${color}` : 'none' }} />
                {label}
              </span>
            ))}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

const Note = ({ children }) => <p style={{ fontSize: 12, color: C.faint, margin: '10px 0 0', lineHeight: 1.5 }}>{children}</p>;

function Warn({ children }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: C.warnSoft, border: '1px solid #FED7AA', borderRadius: 12, padding: '12px 14px', fontSize: 13, color: C.ink2, marginBottom: 12 }}>
      <AlertTriangle size={16} color={C.warn} style={{ flexShrink: 0, marginTop: 1 }} />
      <div>{children}</div>
    </div>
  );
}

const LokalTag = () => (
  <span style={{ fontSize: 10, fontWeight: 700, color: C.accent, background: C.accentSoft, borderRadius: 5, padding: '2px 6px', marginRight: 6, letterSpacing: '.03em' }}>LOKAL</span>
);

/* ── Ay aralığı filtresi ─────────────────────────────────────────── */

// 'YYYY-MM' + k ay
const ymShift = (s, k) => {
  const d = new Date(Date.UTC(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1 + k, 1));
  return d.toISOString().slice(0, 7);
};
const ymList = (a, b) => { const out = []; for (let m = a; m <= b; m = ymShift(m, 1)) out.push(m); return out; };

const selectStyle = {
  ...{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 8 },
  padding: '6px 10px', fontSize: 13, color: C.ink2, fontFamily: FONT, cursor: 'pointer',
};

function MonthRange({ range, available, onChange, disabled }) {
  const max = available?.max || new Date().toISOString().slice(0, 7);
  // Seçenekler: GBP verisinin başladığı ay (yoksa 12 ay geri) → içinde bulunulan ay
  const min = available?.gbpFrom && available.gbpFrom < max ? available.gbpFrom : ymShift(max, -11);
  const opts = ymList(min < range.from ? min : range.from, max);
  const presets = [
    ['Son 3 ay', ymShift(max, -2), max],
    ['Son 6 ay', ymShift(max, -5), max],
    ['Tümü', min, max],
  ];
  const chip = (on) => ({
    padding: '6px 10px', fontSize: 12.5, fontWeight: 500, borderRadius: 8, fontFamily: FONT, cursor: 'pointer',
    border: `1px solid ${on ? C.accent : C.line}`, background: on ? C.accentSoft : '#fff', color: on ? C.accent : C.ink2,
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', opacity: disabled ? 0.6 : 1 }}>
      <span style={{ fontSize: 13, color: C.muted }}>Dönem</span>
      <select value={range.from} disabled={disabled} style={selectStyle} aria-label="Başlangıç ayı"
        onChange={(e) => onChange({ from: e.target.value, to: e.target.value > range.to ? e.target.value : range.to })}>
        {opts.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
      </select>
      <span style={{ color: C.faint }}>–</span>
      <select value={range.to} disabled={disabled} style={selectStyle} aria-label="Bitiş ayı"
        onChange={(e) => onChange({ to: e.target.value, from: e.target.value < range.from ? e.target.value : range.from })}>
        {opts.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
      </select>
      {presets.map(([label, f, t]) => (
        <button key={label} type="button" disabled={disabled} style={chip(range.from === f && range.to === t)}
          onClick={() => onChange({ from: f, to: t })}>{label}</button>
      ))}
    </div>
  );
}

/* ── Sayfa ───────────────────────────────────────────────────────── */

function LocalPerformance({ brandId, brandName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAllCamps, setShowAllCamps] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  // Ay aralığı; null = backend varsayılanı (son 7 ay)
  const [range, setRange] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = range ? `?from=${range.from}&to=${range.to}` : '';
      setData(await getJson(`/api/yatas-planning/gbp-local${qs}`, brandId));
    } catch (e) {
      toast.error('Veri yüklenemedi: ' + e.message);
    } finally { setLoading(false); }
  }, [brandId, range]);

  useEffect(() => { load(); }, [load]);

  // Yenileme sürerken 10 sn'de bir durum sor
  useEffect(() => {
    if (!data?.refresh?.running) return undefined;
    const t = setTimeout(load, 10000);
    return () => clearTimeout(t);
  }, [data, load]);

  const refresh = async () => {
    try {
      const res = await getJson('/api/yatas-planning/gbp-local/refresh', brandId, { method: 'POST' });
      if (res && res.started === false) toast('Yenileme zaten çalışıyor.', { icon: 'ℹ️' });
      else toast.success('GBP verisi çekiliyor — lokasyon sayısına göre birkaç dakika sürer.');
      load();
    } catch (e) {
      toast.error('Başlatılamadı: ' + e.message);
    }
  };

  const v = useMemo(() => {
    if (!data) return null;
    const curYm = new Date().toISOString().slice(0, 7);
    const series = data.series || [];
    // Kısmi (devam eden) ay: backend partial işaretler; eski yanıtta işaret yoksa içinde bulunulan ay
    const isPartial = (x) => (x.partial != null ? x.partial : x.month >= curYm);
    const closed = series.filter((x) => !isPartial(x));
    const partialRow = series.find(isPartial) || null;
    // Çubuk grafikler için: kısmi ay soluk çizilir
    const withPartial = partialRow ? [...closed, partialRow] : closed;
    const gbpRows = closed.filter((s) => s.directions != null);
    const adRows = closed.filter((s) => s.adDirections != null);
    // Endeks: her seri kendi ilk sıfır olmayan ayına göre 100
    const indexOf = (key) => {
      const base = closed.find((x) => x[key])?.[key];
      return (x) => (base && x[key] != null ? (x[key] / base) * 100 : null);
    };
    const ic = indexOf('localCost'), ig = indexOf('directions'), ia = indexOf('adLocalDirections');
    const trend = closed.map((x) => ({ ...x, iCost: ic(x), iGbp: ig(x), iAdLocal: ia(x) }));
    const last = closed.at(-1);
    const prev = closed.at(-2);
    const topMax = Math.max(1, ...(data.topLocations || []).map((l) => l.directions || 0));
    // Seçili dönem toplamları (kısmi ay dahil)
    const sum = (k) => { const xs = withPartial.filter((x) => x[k] != null); return xs.length ? xs.reduce((t, x) => t + x[k], 0) : null; };
    const totals = { directions: sum('directions'), calls: sum('calls'), website: sum('website') };
    return { closed, partialRow, withPartial, gbpRows, adRows, trend, last, prev, topMax, totals };
  }, [data]);

  if (loading && !data) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 0', color: C.faint, fontFamily: FONT }}><Loader2 className="animate-spin" size={18} style={{ marginRight: 8 }} /> Yükleniyor…</div>;
  }
  if (!data || !v) return null;

  const r = data.refresh;
  const cov = data.coverage;
  const failed = cov?.failed || [];
  const byReason = Object.entries(cov?.byReason || {});
  const gbpDate = ddmm(data.gbpThrough);
  const partialDate = ddmm(v.partialRow?.gbpThrough) || gbpDate;
  const partialTag = v.partialRow ? `${monthLabel(v.partialRow.month)} (devam ediyor${partialDate ? `, ${partialDate}'e kadar` : ''})` : '';
  const { partialRow, withPartial, gbpRows, adRows, trend, last, prev, topMax } = v;
  const g = gbpRows.at(-1), gPrev = gbpRows.at(-2);
  const a = adRows.at(-1), aPrev = adRows.at(-2);
  const adErrors = (data.adAccounts || []).filter((x) => !x.ok);
  const camps = data.adCampaigns || [];
  const costPerDir = last && last.adLocalDirections ? last.localCost / last.adLocalDirections : null;
  const prevCostPerDir = prev && prev.adLocalDirections ? prev.localCost / prev.adLocalDirections : null;
  // Reklamlı yol tarifi GBP toplamını aşıyorsa GBP çekimi eksik (izin hatası alan lokasyonlar vb.)
  const gbpShort = g && a && g.month === a.month && a.adDirections > g.directions;
  // Kısmi ay: ad satırı varsa soluk çubukla eklenir
  const adRowsP = partialRow && partialRow.adDirections != null ? [...adRows, partialRow] : adRows;
  const labelP = (ym) => {
    const x = withPartial.find((y) => y.month === ym);
    return x?.partial ? `${monthLabel(ym)} (devam ediyor${ddmm(x.gbpThrough) ? `, ${ddmm(x.gbpThrough)}'e kadar` : ''})` : monthLabel(ym);
  };
  const lastMonth = last ? monthLabel(last.month) : '';
  const months = v.closed.length;
  const rng = range || data.range;
  const rangeText = rng ? (rng.from === rng.to ? monthLabel(rng.from) : `${monthLabel(rng.from)} – ${monthLabel(rng.to)}`) : '';
  const topRangeText = data.topRange
    ? (data.topRange.from === data.topRange.to ? monthLabel(data.topRange.from) : `${shortMonth(data.topRange.from)} – ${shortMonth(data.topRange.to)} toplamı`)
    : (data.topMonth ? monthLabel(data.topMonth) : '');
  const totalSub = (k) => (v.totals[k] != null && (v.withPartial.length > 1) ? `Seçili dönem: ${num(v.totals[k])}` : undefined);

  return (
    <div style={{ fontFamily: FONT, color: C.ink }}>
      {/* Başlık */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 13, color: C.muted }}>{brandName} · Business Profile</div>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: '4px 0 6px', letterSpacing: '-0.01em' }}>Lokal Performans</h1>
          <div style={{ fontSize: 13, color: C.muted }}>
            {rangeText} · {months} kapanmış ay · GBP Performance + canlı Google Ads API
            {gbpDate
              ? <> · GBP verisi {gbpDate}'e kadar{data.gbpThroughEstimated ? ' (tahmini)' : ''}</>
              : data.lastCapturedAt && <> · GBP verisi {fmtDateTime(data.lastCapturedAt)}</>}
          </div>
          <div style={{ fontSize: 12, color: C.faint, marginTop: 3 }}>
            {r?.finishedAt && <>Son yenileme: {fmtDateTime(r.finishedAt)}{TRIGGER_TR[r.trigger] ? ` (${TRIGGER_TR[r.trigger]})` : ''} · </>}
            Her gün 05:10'da otomatik yenilenir.
          </div>
        </div>
        <button onClick={refresh} disabled={r?.running}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, ...kart, padding: '8px 14px', fontSize: 13, fontWeight: 500, color: C.ink2, cursor: r?.running ? 'default' : 'pointer', fontFamily: FONT, opacity: r?.running ? 0.7 : 1 }}>
          {r?.running ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {r?.running ? `GBP çekiliyor ${r.done}/${r.total || '…'}` : 'GBP verisini güncelle'}
        </button>
      </div>

      {rng && (
        <div style={{ ...kart, padding: '10px 14px', marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <MonthRange range={rng} available={data.available} disabled={loading} onChange={setRange} />
          {loading && <Loader2 size={14} className="animate-spin" color={C.faint} />}
          <span style={{ fontSize: 12, color: C.faint, marginLeft: 'auto' }}>
            GBP verisi aylık tutulur{data.available?.gbpFrom ? `; ${monthLabel(data.available.gbpFrom)} öncesi için GBP verisi yok` : ''}.
          </span>
        </div>
      )}

      {r?.interrupted && !r?.running && (
        <div style={{ marginTop: 14 }}>
          <Warn>Son yenileme yarıda kesildi{r.done != null && r.total ? ` (${r.done}/${r.total} lokasyon)` : ''}; tekrar başlatabilirsiniz. "GBP verisini güncelle" düğmesini kullanın.</Warn>
        </div>
      )}

      {/* Kapsam */}
      {cov && (
        <div style={{ ...kart, padding: '12px 16px', marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13, color: C.ink2 }}>
            <span style={{ fontWeight: 600, color: C.ink }}>Kapsam</span>
            <span>{num(cov.fetched)} / {num(cov.expected)} lokasyon çekildi</span>
            {failed.length === 0
              ? <span style={{ color: C.up, fontWeight: 600 }}>· tüm lokasyonlar çekildi</span>
              : <span style={{ color: C.warn, fontWeight: 600 }}>· {num(failed.length)} lokasyon başarısız</span>}
          </div>
          {failed.length > 0 && (
            <>
              {byReason.map(([reason, n]) => (
                <div key={reason} style={{ fontSize: 12.5, color: C.ink2, marginTop: 8, lineHeight: 1.5 }}>
                  <b>{num(n)}</b> lokasyon: {reason}
                  {reason === PERMISSION_ERR && (
                    <div style={{ color: C.muted }}>
                      Bu lokasyonlarda bağlı Google hesabının Business Profile Performance yetkisi yok; toplamlar bu lokasyonlar hariç.
                      Çözüm: GBP'de hesaba yönetici yetkisi verilmesi.
                    </div>
                  )}
                </div>
              ))}
              <button onClick={() => setShowFailed((x) => !x)}
                style={{ border: 0, background: 'none', color: C.accent, fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: '8px 0 0', fontFamily: FONT, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {showFailed ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Başarısız lokasyonlar ({failed.length})
              </button>
              {showFailed && (
                <div style={{ maxHeight: 240, overflowY: 'auto', marginTop: 6, border: `1px solid ${C.hair}`, borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      {failed.map((f) => (
                        <tr key={f.locationId}>
                          <td style={{ ...td, textAlign: 'left', color: C.ink, whiteSpace: 'normal' }}>{f.title || f.locationId}</td>
                          <td style={{ ...td, color: C.muted }} title={f.error}>HTTP {f.httpStatus ?? '–'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* 1. GBP */}
      <Section step="1 · TÜM AKSİYONLAR" title="Google Business Profile"
        sub={`Haritalar ve Arama'daki işletme profillerinden gelen aksiyonlar${g ? ` · ${monthLabel(g.month)}, önceki aya göre` : ''}`}>
        {gbpShort && (
          <Warn>
            {monthLabel(g.month)} GBP yol tarifi ({num(g.directions)}), yalnız reklamdan gelen yol tariflerinden ({num(a.adDirections)}) düşük.
            Bu normalde mümkün değil; GBP çekimi eksik olabilir (izin hatası veren veya doğrulanmamış lokasyonlar toplama girmiyor).
          </Warn>
        )}
        {!gbpRows.length && !r?.running && (
          <Warn>GBP yol tarifi verisi henüz çekilmedi. "GBP verisini güncelle" ile başlatın; tüm lokasyonların son 7 ayı çekilir.</Warn>
        )}
        {g && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <Kpi label="Yol tarifi" value={num(g.directions)} change={pctChange(g.directions, gPrev?.directions)} color={M.directions} sub={totalSub('directions')} />
              <Kpi label="Arama tıklaması" value={num(g.calls)} change={pctChange(g.calls, gPrev?.calls)} color={M.calls} sub={totalSub('calls')} />
              <Kpi label="Web sitesi tıklaması" value={num(g.website)} change={pctChange(g.website, gPrev?.website)} color={M.website} sub={totalSub('website')} />
              <Kpi label="Lokasyon" value={num(g.locations)} sub="veri gelen profil sayısı" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 12, marginTop: 12 }}>
              <Panel title="Aylık GBP aksiyonları" legend={[['Yol tarifi', M.directions], ['Arama', M.calls], ['Web sitesi', M.website]]}>
                <div style={{ height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={gbpRows} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={C.hair} vertical={false} />
                      <XAxis dataKey="month" tickFormatter={shortMonth} tick={axisTick} axisLine={{ stroke: C.line }} tickLine={false} />
                      <YAxis tickFormatter={compact} tick={axisTick} axisLine={false} tickLine={false} width={40} />
                      <Tooltip contentStyle={tooltipStyle} labelFormatter={monthLabel} formatter={(val) => num(val)} />
                      <Line isAnimationActive={false} dataKey="directions" name="Yol tarifi" stroke={M.directions} strokeWidth={2.5} dot={{ r: 3 }} />
                      <Line isAnimationActive={false} dataKey="calls" name="Arama" stroke={M.calls} strokeWidth={2} dot={{ r: 3 }} />
                      <Line isAnimationActive={false} dataKey="website" name="Web sitesi" stroke={M.website} strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                {partialRow && <Note>{partialTag} grafiğe alınmadı; tam ayla kıyaslanmaz. Değişim rozetleri son tam ayı önceki tam ayla karşılaştırır.</Note>}
              </Panel>
              <Panel title={<>En çok yol tarifi alan lokasyonlar {topRangeText && <span style={{ fontSize: 12, fontWeight: 400, color: C.faint }}>· {topRangeText}</span>}</>}>
                {data.topLocations.length === 0
                  ? <div style={{ fontSize: 13, color: C.faint }}>Henüz veri yok.</div>
                  : (
                    <div style={{ maxHeight: 268, overflowY: 'auto', margin: '0 -18px -18px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ ...th, textAlign: 'left', paddingLeft: 18 }}>Lokasyon</th>
                            <th style={th}>Yol tarifi</th>
                            <th style={{ ...th, paddingRight: 18 }}>Arama</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.topLocations.map((l) => (
                            <tr key={l.location_id}>
                              <td style={{ ...td, textAlign: 'left', paddingLeft: 18, color: C.ink, whiteSpace: 'normal' }}>
                                <MapPin size={12} color={C.faint} style={{ display: 'inline', marginRight: 6, verticalAlign: -1 }} />{l.title}
                              </td>
                              <td style={td}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ width: 60, height: 6, borderRadius: 3, background: C.hair, overflow: 'hidden', display: 'inline-block' }}>
                                    <span style={{ display: 'block', height: '100%', width: `${((l.directions || 0) / topMax) * 100}%`, background: M.directions }} />
                                  </span>
                                  {num(l.directions)}
                                </span>
                              </td>
                              <td style={{ ...td, paddingRight: 18, color: C.muted }}>{num(l.calls)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
              </Panel>
            </div>
          </>
        )}
      </Section>

      {/* 2. Reklamlı aksiyonlar */}
      <Section step="2 · REKLAMDAN GELEN" title="Reklamlı lokasyon aksiyonları"
        sub={`Google Ads'in reklam etkileşimine bağladığı aksiyonlar (Google'ın kendi ölçümü)${a ? ` · ${monthLabel(a.month)}, önceki aya göre` : ''}`}>
        {adErrors.length > 0 && (
          <Warn>{adErrors.map((x) => <div key={x.account}>Google Ads {x.account}: {x.error}</div>)}</Warn>
        )}
        {!adRows.length && adErrors.length === 0 && (
          <div style={{ ...kart, padding: 18, fontSize: 13, color: C.faint }}>
            Bu markanın Google Ads hesaplarında lokasyon dönüşümü (yol tarifi, mağaza ziyareti, tıkla-ara) bulunamadı.
          </div>
        )}
        {a && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <Kpi label="Yol tarifi" value={num(a.adDirections)} change={pctChange(a.adDirections, aPrev?.adDirections)} color={M.directions}
                sub={`${num(a.adLocalDirections)} lokal kampanyalardan`} />
              <Kpi label="Mağaza ziyareti" value={num(a.adStoreVisits)} change={pctChange(a.adStoreVisits, aPrev?.adStoreVisits)} color={M.storeVisits}
                sub={`Google tahmini · ${num(a.adLocalStoreVisits)} lokal kampanyalardan`} />
              <Kpi label="Tıkla-ara" value={num(a.adCalls)} change={pctChange(a.adCalls, aPrev?.adCalls)} color={M.calls} />
              <Kpi label="Lokal web sitesi ziyareti" value={num(a.adWebsite)} change={pctChange(a.adWebsite, aPrev?.adWebsite)} color={M.website}
                sub={a.adWebsite ? 'işletme profili üzerinden' : 'bu hesapta ölçülmüyor'} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 12, marginTop: 12 }}>
              <Panel title="Aylık reklamlı aksiyonlar" legend={[['Yol tarifi', M.directions], ['Mağaza ziyareti', M.storeVisits], ['Tıkla-ara', M.calls]]}>
                <div style={{ height: 268 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={adRowsP} margin={{ top: 6, right: 8, left: 0, bottom: 0 }} barGap={2} barCategoryGap="22%">
                      <CartesianGrid stroke={C.hair} vertical={false} />
                      <XAxis dataKey="month" tickFormatter={shortMonth} tick={axisTick} axisLine={{ stroke: C.line }} tickLine={false} />
                      <YAxis tickFormatter={compact} tick={axisTick} axisLine={false} tickLine={false} width={40} />
                      <Tooltip contentStyle={tooltipStyle} labelFormatter={labelP} formatter={(val) => num(val)} cursor={{ fill: C.accentSoft, opacity: 0.5 }} />
                      <Bar isAnimationActive={false} dataKey="adDirections" name="Yol tarifi" fill={M.directions} radius={[3, 3, 0, 0]}>
                        {adRowsP.map((x) => <Cell key={x.month} fillOpacity={x.partial ? 0.35 : 1} />)}
                      </Bar>
                      <Bar isAnimationActive={false} dataKey="adStoreVisits" name="Mağaza ziyareti" fill={M.storeVisits} radius={[3, 3, 0, 0]}>
                        {adRowsP.map((x) => <Cell key={x.month} fillOpacity={x.partial ? 0.35 : 1} />)}
                      </Bar>
                      <Bar isAnimationActive={false} dataKey="adCalls" name="Tıkla-ara" fill={M.calls} radius={[3, 3, 0, 0]}>
                        {adRowsP.map((x) => <Cell key={x.month} fillOpacity={x.partial ? 0.35 : 1} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
              <Panel title={<>Kampanya bazında <span style={{ fontSize: 12, fontWeight: 400, color: C.faint }}>· son 7 ay</span></>}>
                <div style={{ maxHeight: showAllCamps ? 'none' : 268, overflowY: 'auto', margin: '0 -18px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, textAlign: 'left', paddingLeft: 18 }}>Kampanya</th>
                        <th style={th}>Yol tarifi</th>
                        <th style={th}>Mağaza z.</th>
                        <th style={{ ...th, paddingRight: 18 }}>Tıkla-ara</th>
                      </tr>
                    </thead>
                    <tbody>
                      {camps.map((c) => (
                        <tr key={c.campaign}>
                          <td style={{ ...td, textAlign: 'left', paddingLeft: 18, color: C.ink, whiteSpace: 'normal', fontSize: 12.5 }}>
                            {c.local && <LokalTag />}{c.campaign}
                          </td>
                          <td style={{ ...td, color: C.ink, fontWeight: 600 }}>{num(c.directions)}</td>
                          <td style={td}>{num(c.storeVisits)}</td>
                          <td style={{ ...td, paddingRight: 18 }}>{num(c.calls)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {camps.length > 7 && (
                  <button onClick={() => setShowAllCamps((x) => !x)}
                    style={{ border: 0, background: 'none', color: C.accent, fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: '10px 0 0', fontFamily: FONT }}>
                    {showAllCamps ? 'Daralt' : `Tümünü göster (${camps.length})`}
                  </button>
                )}
              </Panel>
            </div>
          </>
        )}
        <Note>
          Kaynak Google Ads, tüm dönüşümler sütunu. Mağaza ziyareti Google'ın modellediği bir tahmindir. Meta'da mağazaya yönlendirme ölçümü
          olmadığı için bu bölüm yalnız Google'dır.{adRowsP !== adRows && ' Soluk çubuk devam eden aydır.'} Reklamlı yol tarifleri GBP sayısının içinde de olabilir; iki sayı toplanmaz.
        </Note>
      </Section>

      {/* 3. Lokal bütçe */}
      <Section step="3 · BÜTÇE" title="Lokal bütçe"
        sub={`Bütçe olarak yalnız mağaza ziyareti hedefleyen lokal kampanyaların harcaması${last ? ` · ${lastMonth}, önceki aya göre` : ''}`}>
        {last && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
            <Kpi label="Lokal harcama" value={tl(last.localCost)} change={pctChange(last.localCost, prev?.localCost)} tone="neutral" color={M.cost} sub="yalnız lokal kampanyalar" />
            <Kpi label="Lokal kampanyalardan yol tarifi" value={num(last.adLocalDirections)} change={pctChange(last.adLocalDirections, prev?.adLocalDirections)} color={M.adLocal} sub="Google Ads ölçümü" />
            <Kpi label="Yol tarifi başına lokal harcama" value={costPerDir != null ? `₺${nf(costPerDir, 2)}` : '–'} change={pctChange(costPerDir, prevCostPerDir)} tone="good-down" sub="düşmesi iyi" />
            <Kpi label="Lokal kampanya" value={num(data.localCampaigns.length)} sub="son 7 ayda harcama yapan" />
          </div>
        )}
        {partialRow && (
          <div style={{ ...kart, padding: '12px 16px', marginBottom: 12, background: C.accentSoft, borderColor: '#D6E0FB' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{partialTag}</div>
            <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginTop: 6, fontSize: 13, color: C.ink2 }}>
              <span title="Lokal harcama GBP verisinin bittiği tarihe kırpıldı; böylece yol tarifiyle aynı günleri kapsar.">
                Lokal harcama{partialDate ? ` (${partialDate}'e kadar)` : ''}: <b>{tl(partialRow.localCostThrough ?? partialRow.localCost)}</b>
              </span>
              <span>Lokal kampanyalardan yol tarifi: <b>{num(partialRow.adLocalDirections)}</b></span>
              <span>GBP yol tarifi: <b>{num(partialRow.directions)}</b></span>
              <span>Yol tarifi başına lokal harcama: <b>{partialRow.costPerDirection != null ? `₺${nf(partialRow.costPerDirection, 2)}` : '–'}</b></span>
            </div>
            <div style={{ fontSize: 12, color: C.faint, marginTop: 4 }}>
              {partialRow.localCostThrough != null ? 'Harcama karşılaştırması GBP verisinin bittiği tarihe kırpılmış lokal harcamayla yapılır.' : ''} Tam aylarla kıyaslanmaz.
            </div>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 12 }}>
          <Panel title="Lokal harcama ve lokasyona gönderme birlikte artıyor mu?" style={{ gridColumn: '1 / -1' }}
            legend={[['Lokal harcama', M.cost], ['GBP yol tarifi (tümü)', M.directions], ['Lokal kampanyalardan yol tarifi', M.adLocal, true]]}>
            <div style={{ fontSize: 12, color: C.faint, marginTop: -6, marginBottom: 8 }}>
              Ölçekler çok farklı olduğu için her çizgi kendi ilk ayına göre endekslendi ({trend[0] ? monthLabel(trend[0].month) : 'ilk ay'} = 100) · gerçek değerler üzerine gelince görünür
            </div>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={C.hair} vertical={false} />
                  <XAxis dataKey="month" tickFormatter={shortMonth} tick={axisTick} axisLine={{ stroke: C.line }} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} width={40} domain={['auto', 'auto']} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={monthLabel}
                    formatter={(val, n, p) => {
                      const raw = p.payload[{ iCost: 'localCost', iGbp: 'directions', iAdLocal: 'adLocalDirections' }[p.dataKey]];
                      return [`${n === 'Lokal harcama' ? tl(raw) : num(raw)} · endeks ${Math.round(val)}`, n];
                    }} />
                  <ReferenceLine y={100} stroke={C.line} strokeDasharray="4 4" />
                  <Line isAnimationActive={false} dataKey="iCost" name="Lokal harcama" stroke={M.cost} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                  <Line isAnimationActive={false} dataKey="iGbp" name="GBP yol tarifi (tümü)" stroke={M.directions} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                  <Line isAnimationActive={false} dataKey="iAdLocal" name="Lokal kampanyalardan yol tarifi" stroke={M.adLocal} strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <Note>İlişki yön gösterir, birebir atıf değildir. Devam eden ay eksik olduğu için grafiğe alınmadı{partialRow ? ` (${partialTag})` : ''}.</Note>
          </Panel>
          <Panel title="Aylık lokal harcama" legend={[['Lokal harcama', M.cost]]}>
            {partialRow && <div style={{ fontSize: 12, color: C.faint, marginTop: -6, marginBottom: 8 }}>Soluk çubuk: {partialTag}</div>}
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={withPartial} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={C.hair} vertical={false} />
                  <XAxis dataKey="month" tickFormatter={shortMonth} tick={axisTick} axisLine={{ stroke: C.line }} tickLine={false} />
                  <YAxis tickFormatter={(x) => `₺${compact(x)}`} tick={axisTick} axisLine={false} tickLine={false} width={52} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={labelP}
                    formatter={(val, n, p) => (p.payload.partial && p.payload.localCostThrough != null
                      ? [`${tl(val)} (gbp'ye kırpılmış: ${tl(p.payload.localCostThrough)})`, n] : tl(val))}
                    cursor={{ fill: C.accentSoft, opacity: 0.5 }} />
                  <Bar isAnimationActive={false} dataKey="localCost" name="Lokal harcama" fill={M.cost} radius={[3, 3, 0, 0]}>
                    {withPartial.map((x) => <Cell key={x.month} fillOpacity={x.partial ? 0.35 : 1} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
          <Panel title={<>Lokal kampanyalar <span style={{ fontSize: 12, fontWeight: 400, color: C.faint }}>· son 7 ay harcama</span></>}>
            {data.localCampaigns.length === 0
              ? <div style={{ fontSize: 13, color: C.faint }}>Lokal kampanya bulunamadı.</div>
              : (
                <div style={{ maxHeight: 240, overflowY: 'auto', margin: '0 -18px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, textAlign: 'left', paddingLeft: 18 }}>Mecra</th>
                        <th style={{ ...th, textAlign: 'left' }}>Kampanya</th>
                        <th style={{ ...th, paddingRight: 18 }}>Harcama</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.localCampaigns.map((c) => (
                        <tr key={c.campaign}>
                          <td style={{ ...td, textAlign: 'left', paddingLeft: 18, color: C.muted, width: 70 }}>{c.platform}</td>
                          <td style={{ ...td, textAlign: 'left', color: C.ink, whiteSpace: 'normal', fontSize: 12.5 }}>{c.campaign}</td>
                          <td style={{ ...td, paddingRight: 18, color: C.ink, fontWeight: 600 }}>{tl(c.cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </Panel>
        </div>
        <Note>{data.rule}</Note>
      </Section>
    </div>
  );
}

export default function YatasLocalPerformance() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id;
  if (!brandId || !YATAS_GROUP_BRAND_IDS.has(brandId)) {
    return <div style={{ padding: 32, textAlign: 'center', color: C.faint, fontSize: 14, fontFamily: FONT }}>Bu sayfaya erişim yetkiniz bulunmuyor.</div>;
  }
  return (
    <div style={{ background: C.ground, minHeight: '100%', padding: '28px 32px 48px' }}>
      <div style={{ maxWidth: 1320, margin: '0 auto' }}>
        {/* key={brandId}: marka değişince veri sıfırlansın */}
        <LocalPerformance key={brandId} brandId={brandId} brandName={selectedBrand?.name} />
      </div>
    </div>
  );
}
